import { useState, useEffect } from "react";
import axios from "axios";
import {
  Lock,
  RefreshCw,
  Inbox,
  Mail,
  Phone,
  ChevronDown,
  ChevronUp,
  Search,
  X,
  CalendarClock,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function humanize(key) {
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatVal(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}

const TRIP_DATE_KEYS = [
  "pickup_date",
  "arrival_date",
  "departure_date",
  "cruise_date",
  "ceremony_date",
  "event_date",
  "arrival_pickup_date",
  "departure_pickup_date",
  "date",
];

// First valid YYYY-MM-DD trip date found in the booking details.
function tripYmd(b) {
  const d = b.details || {};
  for (const k of TRIP_DATE_KEYS) {
    const v = d[k];
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
  }
  return null;
}

const TRIP_TIME_KEYS = [
  "pickup_time",
  "arrival_pickup_time",
  "arrival_time",
  "departure_pickup_time",
  "departure_time",
  "cruise_time",
  "ceremony_time",
  "event_start_time",
  "time",
];

function prettyDate(ymd) {
  const t = new Date(`${ymd}T00:00:00`);
  if (isNaN(t)) return null;
  return t.toLocaleDateString("en-AU", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function prettyTime(v) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(v || ""));
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m[2]} ${suffix}`;
}

// Scannable trip timing legs for a booking card. Return/Both shows both legs.
function tripLegs(b) {
  const d = b.details || {};
  if (d.transfer_type === "Return (Both)") {
    const legs = [];
    if (d.arrival_date) {
      legs.push({ label: "Arrival", date: prettyDate(d.arrival_date), time: prettyTime(d.arrival_pickup_time) });
    }
    if (d.departure_date) {
      legs.push({ label: "Return", date: prettyDate(d.departure_date), time: prettyTime(d.departure_pickup_time) });
    }
    return legs.filter((l) => l.date);
  }
  const ymd = tripYmd(b);
  if (!ymd) return [];
  let time = null;
  for (const k of TRIP_TIME_KEYS) {
    if (d[k]) {
      time = prettyTime(d[k]);
      if (time) break;
    }
  }
  const date = prettyDate(ymd);
  return date ? [{ label: null, date, time }] : [];
}

// All dated "moments" of a booking. Return/Both contributes both legs.
function tripMoments(b) {
  const d = b.details || {};
  if (d.transfer_type === "Return (Both)") {
    const out = [];
    if (typeof d.arrival_date === "string" && /^\d{4}-\d{2}-\d{2}/.test(d.arrival_date)) {
      out.push({ ymd: d.arrival_date.slice(0, 10), time: d.arrival_pickup_time || "" });
    }
    if (typeof d.departure_date === "string" && /^\d{4}-\d{2}-\d{2}/.test(d.departure_date)) {
      out.push({ ymd: d.departure_date.slice(0, 10), time: d.departure_pickup_time || "" });
    }
    return out;
  }
  const ymd = tripYmd(b);
  if (!ymd) return [];
  let time = "";
  for (const k of TRIP_TIME_KEYS) {
    if (typeof d[k] === "string" && /^\d{1,2}:\d{2}/.test(d[k])) {
      time = d[k];
      break;
    }
  }
  return [{ ymd, time }];
}

function momentSortKey(m) {
  const t = /^(\d{1,2}):(\d{2})/.exec(m.time || "");
  const hhmm = t ? `${t[1].padStart(2, "0")}:${t[2]}` : "23:59";
  return `${m.ymd}T${hhmm}`;
}

// The customer-relevant pickup moment: the next upcoming leg if any,
// otherwise the most recent past one.
function relevantMoment(b, todayYmd) {
  const ms = [...tripMoments(b)].sort((a, x) =>
    momentSortKey(a).localeCompare(momentSortKey(x)),
  );
  if (!ms.length) return null;
  const upcoming = ms.find((m) => m.ymd >= todayYmd);
  return upcoming || ms[ms.length - 1];
}

const STATUS_FILTERS = [
  { id: "all", label: "All statuses" },
  { id: "new", label: "Pending" },
  { id: "confirmed", label: "Confirmed" },
  { id: "cancelled", label: "Cancelled" },
];

const TIME_FILTERS = [
  { id: "all", label: "All dates" },
  { id: "today", label: "Today" },
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
];

export default function Admin() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeScope, setTimeScope] = useState("upcoming");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`, { withCredentials: true });
      setItems(res.data || []);
      setAuthed(true);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthed(false);
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAuthed(true);
        fetchAll();
      } catch {
        setAuthed(false);
      } finally {
        setChecking(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) return toast.error("Enter your email/username and password");
    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/login`,
        { identifier: identifier.trim(), password },
        { withCredentials: true },
      );
      setPassword("");
      setAuthed(true);
      fetchAll();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      /* cookie clear is best-effort */
    }
    setAuthed(false);
    setItems([]);
    setIdentifier("");
    setPassword("");
  };

  const todayYmd = new Date().toLocaleDateString("en-CA");
  const q = search.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, "");

  const matchesSearch = (b) => {
    if (!q) return true;
    if ((b.name || "").toLowerCase().includes(q)) return true;
    if ((b.email || "").toLowerCase().includes(q)) return true;
    if ((b.id || "").toLowerCase().includes(q)) return true;
    if ((b.phone || "").toLowerCase().includes(q)) return true;
    if (qDigits.length >= 3) {
      const phoneDigits = (b.phone || "").replace(/\D/g, "");
      if (phoneDigits.includes(qDigits)) return true;
      // AU national format (0400...) vs stored +61 400... — retry without the leading 0.
      const alt = qDigits.replace(/^0/, "");
      if (alt.length >= 3 && phoneDigits.includes(alt)) return true;
    }
    return false;
  };

  const filtered = items.filter((b) => {
    if (!matchesSearch(b)) return false;
    if (filter !== "all" && b.request_type !== filter) return false;
    if (statusFilter !== "all" && (b.status || "new") !== statusFilter) return false;
    const rel = relevantMoment(b, todayYmd);
    if (timeScope === "today" && (!rel || rel.ymd !== todayYmd)) return false;
    if (timeScope === "upcoming" && (!rel || rel.ymd < todayYmd)) return false;
    if (timeScope === "past" && (!rel || rel.ymd >= todayYmd)) return false;
    if (dateFrom || dateTo) {
      const ms = tripMoments(b);
      const inRange = ms.some(
        (m) => (!dateFrom || m.ymd >= dateFrom) && (!dateTo || m.ymd <= dateTo),
      );
      if (!inRange) return false;
    }
    return true;
  });

  // Nearest pickup first when looking at current work.
  if (timeScope === "today" || timeScope === "upcoming") {
    filtered.sort((a, b) => {
      const ma = relevantMoment(a, todayYmd);
      const mb = relevantMoment(b, todayYmd);
      return momentSortKey(ma).localeCompare(momentSortKey(mb));
    });
  }

  const filtersActive =
    filter !== "all" ||
    statusFilter !== "all" ||
    timeScope !== "all" ||
    !!dateFrom ||
    !!dateTo ||
    !!q;

  const resetFilters = () => {
    setFilter("all");
    setStatusFilter("all");
    setTimeScope("all");
    setDateFrom("");
    setDateTo("");
    setSearch("");
  };

  const toggle = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const [updating, setUpdating] = useState({});
  const [noteDrafts, setNoteDrafts] = useState({});

  const noteFor = (b) => noteDrafts[b.id] ?? b.customer_note ?? "";

  const applyPatch = async (id, body, successMsg) => {
    setUpdating((u) => ({ ...u, [id]: true }));
    try {
      const res = await axios.patch(`${API}/bookings/${id}`, body, {
        withCredentials: true,
      });
      setItems((list) =>
        list.map((b) =>
          b.id === id
            ? { ...b, status: res.data.status, customer_note: res.data.customer_note }
            : b,
        ),
      );
      setNoteDrafts((n) => {
        const { [id]: _drop, ...rest } = n;
        return rest;
      });
      if (res.data.email_kind) {
        toast.success(`${successMsg} — customer notified by email`);
      } else if (res.data.changed) {
        toast.success(successMsg);
      } else {
        toast.info("No change — no email sent");
      }
    } catch {
      toast.error("Failed to update booking");
    } finally {
      setUpdating((u) => ({ ...u, [id]: false }));
    }
  };

  const updateStatus = async (b, status) => {
    if (
      status === "cancelled" &&
      !window.confirm("Cancel this booking? The customer will receive a cancellation email.")
    )
      return;
    await applyPatch(
      b.id,
      { status, customer_note: noteFor(b) },
      `Booking marked ${status}`,
    );
  };

  const saveNote = async (b) =>
    applyPatch(b.id, { customer_note: noteFor(b) }, "Customer note saved");

  if (checking) {
    return (
      <div data-testid="admin-checking" className="min-h-screen bg-zinc-50 grid place-items-center">
        <RefreshCw className="w-5 h-5 text-zinc-400 animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div
        data-testid="admin-login"
        className="min-h-screen bg-zinc-50 grid place-items-center px-6"
      >
        <Card className="w-full max-w-md p-8 border-zinc-200">
          <div className="w-12 h-12 rounded-full bg-zinc-900 text-white grid place-items-center">
            <Lock className="w-5 h-5" />
          </div>
          <h1
            className="mt-5 text-2xl font-semibold text-zinc-900"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Admin Sign-In
          </h1>
          <p className="mt-2 text-sm text-zinc-600">
            Sign in to view bookings and quote requests.
          </p>
          <form onSubmit={handleLogin} className="mt-6 space-y-4">
            <Input
              data-testid="admin-identifier-input"
              type="text"
              autoComplete="username"
              placeholder="Email or username"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className="border-zinc-200"
            />
            <Input
              data-testid="admin-password-input"
              type="password"
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="border-zinc-200"
            />
            <Button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full sjc-gold-bg hover:opacity-95 text-zinc-900 font-medium rounded-lg"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-block w-9 h-9 rounded-md bg-zinc-900 text-white grid place-items-center font-semibold">
              SJ
            </span>
            <div>
              <p className="text-sm font-semibold text-zinc-900">
                Sydney James Chauffeurs
              </p>
              <p className="text-xs text-zinc-500">Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              data-testid="admin-refresh-btn"
              variant="outline"
              onClick={() => fetchAll()}
              disabled={loading}
              className="border-zinc-200"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              data-testid="admin-logout-btn"
              variant="outline"
              onClick={handleLogout}
              className="border-zinc-200 text-zinc-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2
              className="text-2xl md:text-3xl font-semibold text-zinc-900"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Bookings &amp; Quotes
            </h2>
            <p className="text-sm text-zinc-500 mt-1">
              {items.length} total ·{" "}
              {items.filter((i) => i.request_type === "booking").length} bookings ·{" "}
              {items.filter((i) => i.request_type === "quote").length} quotes
            </p>
          </div>
          <div className="flex gap-2">
            {[
              { id: "all", label: "All" },
              { id: "booking", label: "Bookings" },
              { id: "quote", label: "Quotes" },
            ].map((f) => (
              <button
                key={f.id}
                data-testid={`admin-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f.id
                    ? "bg-zinc-900 text-white"
                    : "bg-white text-zinc-700 border border-zinc-200 hover:bg-zinc-100"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <Card className="mt-6 p-4 border-zinc-200">
          <div className="relative">
            <label htmlFor="admin-search" className="sr-only">
              Search bookings
            </label>
            <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <Input
              id="admin-search"
              data-testid="admin-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search bookings — name, email, phone or booking reference"
              className="pl-9 pr-9 border-zinc-200"
            />
            {search && (
              <button
                type="button"
                data-testid="admin-search-clear"
                onClick={() => setSearch("")}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="mt-4 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-6 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium mr-1">
                Status
              </span>
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`admin-status-filter-${f.id}`}
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    statusFilter === f.id
                      ? "bg-zinc-900 text-white"
                      : "bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-100"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium mr-1">
                When
              </span>
              {TIME_FILTERS.map((f) => (
                <button
                  key={f.id}
                  data-testid={`admin-time-filter-${f.id}`}
                  onClick={() => setTimeScope(f.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    timeScope === f.id
                      ? "bg-zinc-900 text-white"
                      : "bg-white text-zinc-600 border border-zinc-200 hover:bg-zinc-100"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-wide text-zinc-400 font-medium">
                Trip date
              </span>
              <input
                type="date"
                data-testid="admin-date-from"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-8 px-2 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-700"
                aria-label="From date"
              />
              <span className="text-xs text-zinc-400">to</span>
              <input
                type="date"
                data-testid="admin-date-to"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-8 px-2 rounded-lg border border-zinc-200 bg-white text-xs text-zinc-700"
                aria-label="To date"
              />
            </div>

            <div className="flex items-center gap-3 lg:ml-auto">
              <span data-testid="admin-results-count" className="text-xs text-zinc-500">
                Showing {filtered.length} of {items.length}
              </span>
              {filtersActive && (
                <button
                  data-testid="admin-reset-filters"
                  onClick={resetFilters}
                  className="text-xs font-medium text-zinc-700 underline underline-offset-2 hover:text-zinc-900"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </Card>

        <div className="mt-8 space-y-4">
          {filtered.length === 0 && (
            <Card
              data-testid="admin-empty-state"
              className="p-10 text-center text-zinc-500 border-zinc-200"
            >
              <Inbox className="w-8 h-8 mx-auto text-zinc-400" />
              {items.length === 0 ? (
                <p className="mt-3">No requests yet.</p>
              ) : q ? (
                <>
                  <p className="mt-3">No bookings found.</p>
                  <button
                    data-testid="admin-empty-clear-search"
                    onClick={() => setSearch("")}
                    className="mt-3 text-sm font-medium text-zinc-700 underline underline-offset-2 hover:text-zinc-900"
                  >
                    Clear search
                  </button>
                </>
              ) : (
                <>
                  <p className="mt-3">No bookings match the selected filters.</p>
                  <button
                    data-testid="admin-empty-reset"
                    onClick={resetFilters}
                    className="mt-3 text-sm font-medium text-zinc-700 underline underline-offset-2 hover:text-zinc-900"
                  >
                    Clear filters
                  </button>
                </>
              )}
            </Card>
          )}

          {filtered.map((b) => {
            const detailEntries = Object.entries(b.details || {});
            const isOpen = !!expanded[b.id];
            return (
              <Card
                key={b.id}
                data-testid={`admin-booking-${b.id}`}
                className="p-6 border-zinc-200 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge
                        className={
                          b.request_type === "quote"
                            ? "bg-amber-100 text-amber-800 hover:bg-amber-100"
                            : "bg-emerald-100 text-emerald-800 hover:bg-emerald-100"
                        }
                      >
                        {b.request_type === "quote" ? "Quote Request" : "Booking"}
                      </Badge>
                      <Badge className="bg-zinc-100 text-zinc-700 hover:bg-zinc-100">
                        {b.service_type}
                      </Badge>
                      <Badge
                        data-testid={`admin-status-${b.id}`}
                        className={
                          b.status === "confirmed"
                            ? "bg-emerald-600 text-white hover:bg-emerald-600"
                            : b.status === "cancelled"
                            ? "bg-red-100 text-red-700 hover:bg-red-100"
                            : "bg-blue-100 text-blue-700 hover:bg-blue-100"
                        }
                      >
                        {(b.status || "new").toUpperCase()}
                      </Badge>
                      <span className="text-xs text-zinc-500">
                        {new Date(b.created_at).toLocaleString()}
                      </span>
                    </div>

                    <h3
                      className="mt-3 text-lg font-semibold text-zinc-900"
                      style={{ fontFamily: "Outfit, sans-serif" }}
                    >
                      {b.name}
                    </h3>
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-zinc-600">
                      <span className="flex items-center gap-2">
                        <Phone className="w-4 h-4 sjc-gold" /> {b.phone}
                      </span>
                      <span className="flex items-center gap-2 break-all">
                        <Mail className="w-4 h-4 sjc-gold" /> {b.email}
                      </span>
                    </div>

                    {tripLegs(b).length > 0 && (
                      <div
                        data-testid={`admin-trip-when-${b.id}`}
                        className="mt-3 flex items-center gap-2 flex-wrap text-sm"
                      >
                        <CalendarClock className="w-4 h-4 sjc-gold flex-shrink-0" />
                        {tripLegs(b).map((leg, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-50 border border-zinc-200 text-zinc-800"
                          >
                            {leg.label && (
                              <span className="text-[10px] uppercase tracking-wide text-zinc-400 font-semibold">
                                {leg.label}
                              </span>
                            )}
                            <span className="font-medium">{leg.date}</span>
                            {leg.time && <span className="text-zinc-500">· {leg.time}</span>}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="mt-3 flex gap-2 flex-wrap text-xs">
                      {b.vehicle_preference && (
                        <span className="px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-700">
                          Vehicle: {b.vehicle_preference}
                        </span>
                      )}
                      <span
                        className={`px-2.5 py-1 rounded-md ${
                          b.email_sent
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-zinc-100 text-zinc-500"
                        }`}
                      >
                        Business email: {b.email_sent ? "sent" : "not sent"}
                      </span>
                      <span
                        className={`px-2.5 py-1 rounded-md ${
                          b.customer_email_sent
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-zinc-100 text-zinc-500"
                        }`}
                      >
                        Customer email: {b.customer_email_sent ? "sent" : "not sent"}
                      </span>
                    </div>

                    <div className="mt-4 space-y-2">
                      <label
                        htmlFor={`note-${b.id}`}
                        className="text-xs font-medium text-zinc-600"
                      >
                        Note to customer{" "}
                        <span className="text-zinc-400 font-normal">
                          (optional — included in their confirmation/update emails)
                        </span>
                      </label>
                      <Textarea
                        id={`note-${b.id}`}
                        data-testid={`admin-note-${b.id}`}
                        value={noteFor(b)}
                        onChange={(e) =>
                          setNoteDrafts((n) => ({ ...n, [b.id]: e.target.value }))
                        }
                        maxLength={500}
                        rows={2}
                        placeholder="e.g. Your chauffeur will meet you at the arrivals hall with a name board."
                        className="text-sm border-zinc-200"
                      />
                    </div>

                    <div className="mt-3 flex gap-2 flex-wrap">
                      {b.status !== "confirmed" && (
                        <Button
                          size="sm"
                          data-testid={`admin-confirm-${b.id}`}
                          disabled={!!updating[b.id]}
                          onClick={() => updateStatus(b, "confirmed")}
                          className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          Confirm booking
                        </Button>
                      )}
                      {b.status !== "cancelled" && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`admin-cancel-${b.id}`}
                          disabled={!!updating[b.id]}
                          onClick={() => updateStatus(b, "cancelled")}
                          className="h-8 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          Cancel booking
                        </Button>
                      )}
                      {noteFor(b) !== (b.customer_note ?? "") && (
                        <Button
                          size="sm"
                          variant="outline"
                          data-testid={`admin-save-note-${b.id}`}
                          disabled={!!updating[b.id]}
                          onClick={() => saveNote(b)}
                          className="h-8 border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                        >
                          Save note
                        </Button>
                      )}
                    </div>

                    {detailEntries.length > 0 && (
                      <div className="mt-4">
                        <button
                          data-testid={`admin-toggle-${b.id}`}
                          type="button"
                          onClick={() => toggle(b.id)}
                          className="inline-flex items-center gap-1 text-sm font-medium text-zinc-700 hover:text-zinc-900"
                        >
                          {isOpen ? "Hide" : "Show"} Trip Details
                          {isOpen ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                        {isOpen && (
                          <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
                            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                              {detailEntries.map(([k, v]) => (
                                <div key={k} className="flex flex-col">
                                  <dt className="text-xs uppercase tracking-wider text-zinc-500">
                                    {humanize(k)}
                                  </dt>
                                  <dd className="text-zinc-800 break-words">
                                    {formatVal(v)}
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        )}
                      </div>
                    )}

                    {b.notes && (
                      <p className="mt-4 text-sm text-zinc-600 bg-zinc-50 rounded-lg p-3">
                        <span className="font-medium text-zinc-700">Notes: </span>
                        {b.notes}
                      </p>
                    )}
                  </div>

                  <div className="flex md:flex-col gap-2">
                    <a
                      href={`tel:${b.phone}`}
                      className="px-3 py-2 rounded-lg bg-zinc-900 text-white text-xs font-medium text-center hover:bg-zinc-800"
                    >
                      Call
                    </a>
                    <a
                      href={`mailto:${b.email}`}
                      className="px-3 py-2 rounded-lg border border-zinc-200 text-zinc-700 text-xs font-medium text-center hover:bg-zinc-50"
                    >
                      Email
                    </a>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
}
